import React, { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { Button } from "../formElements/button"
import { useTranslation } from "next-i18next"
import { AskQuestionProps, AskQuestionStep } from "."
import Textarea, { MaxLength, TextareaProps } from "../formElements/textarea"
import { Events, registerEvent } from "../../analytics"
import { AuthRoleType } from "../../graphql/generated/graphql"
import { AuthContext, RedirectContext } from "../../utils/context"
import { Authentication, AuthenticatonPage } from "../authentication"
import { Modal } from "../modal"

interface QuestionPriceProps {
  isDiscounted: boolean
  price?: number | null
}

export const QuestionPrice = ({ isDiscounted, price }: QuestionPriceProps) => {
  const { t } = useTranslation("profile")
  if (isDiscounted && price && price > 0) {
    return (
      <div tw="flex flex-col items-center space-y-2">
        <p tw="text-md font-bold text-center w-full flex justify-center items-center space-x-1">
          <span data-testid="discounted-price">$0</span>
          <span
            tw="text-base-4 line-through"
            css="text-decoration-thickness: 2px; text-decoration-color: #ff00aa;"
            data-testid="price"
          >
            ${price}
          </span>
          <span tw="px-0.5 text-xl">•</span>
          <span>{t("ask_question.audiogram")}</span>
        </p>
        <p tw="px-2.5 py-1 rounded-full bg-green text-white text-xs font-bold inline">
          {t("ask_question.bonsai_discount")}
        </p>
      </div>
    )
  }

  return (
    <p tw="text-md font-bold text-center w-full flex justify-center items-center space-x-1">
      <span data-testid="price">${price}</span>
      <span tw="px-0.5 text-xl">•</span>
      <span>{t("ask_question.audiogram")}</span>
    </p>
  )
}

export const QuestionForm = (props: AskQuestionProps) => {
  const { token } = useContext(AuthContext)
  const { setData, getData } = useContext(RedirectContext)
  const isAskQuestionData = !!getData().data.questionBody
  const { t } = useTranslation("profile")
  const { questionBody, setQuestionBody, setStep, profile } = props
  const { isTakingQuestions, price } = profile
  const isDiscounted = profile?.authRole === AuthRoleType.Staff
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [isNotDismissable, setNotDismissable] = useState(false)
  const modalProps = {
    show: showAuthModal,
    setShow: setShowAuthModal,
    setNotDismissable: isNotDismissable,
    component: (
      <Authentication
        view={AuthenticatonPage.Join}
        setShow={setShowAuthModal}
        setNotDismissable={setNotDismissable}
        redirectUserAfterCreation={false}
      />
    ),
  }
  const textAreaProps: TextareaProps = {
    name: "question-body-input",
    value: questionBody,
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => setQuestionBody(event.target.value),
    placeholder: t("ask_question.question_form_placeholder"),
    maxLength: MaxLength.Max280,
    disabled: false,
  }

  const handleRedirect = () => {
    if (token) {
      setStep?.(AskQuestionStep.Payment)
    } else {
      setShowAuthModal(true)
      setData({ questionBody })
    }
    registerEvent(Events.SubmittedQuestionBodyInput)
  }

  useEffect(() => {
    if (isAskQuestionData) {
      setData({})
      token && setStep?.(AskQuestionStep.Preparing)
    }
  }, [])

  return (
    <div tw="space-y-1 tablet:(space-y-4) desktop:(space-y-6)" id="ask_question_form" data-testid="question-form">
      <div tw="space-y-2.5 flex flex-col justify-center">
        <div tw="space-y-1">
          <h3 tw="text-lg text-center">{t("ask_question.question_form_headline")}</h3>
          {isTakingQuestions && <QuestionPrice isDiscounted={isDiscounted} price={price} />}
        </div>
        <div tw="flex flex-col items-center justify-center">
          <Textarea {...textAreaProps} />
          <Button
            dataTestId="nextbutton"
            action="Next"
            twStyle={tw`w-full`}
            isDisabled={questionBody.length <= 0}
            onClick={handleRedirect}
          />
          <Modal {...modalProps} />
        </div>
      </div>
    </div>
  )
}
